import React, { useRef, useMemo } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import { oceanNoiseGLSL, getHeightGLSL } from './shaders/oceanNoise.glsl.js';

const floorVertexShader = `
uniform float uTime;
uniform float uSeaSpeed;
uniform float uSeaFreq;
uniform float uSeaHeight;
uniform float uSeaChoppy;
varying vec3 vWorldPosition;
varying vec2 vUv;

${oceanNoiseGLSL}
${getHeightGLSL(2)}

void main() {
    vUv = uv;
    vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;

    // Multi-octave terrain: large rolling hills + medium ridges + small bumps
    float terrain = noise(worldPos.xz * 0.002) * 8.0
                  + noise(worldPos.xz * 0.008) * 4.0
                  + noise(worldPos.xz * 0.025) * 1.5
                  + noise(worldPos.xz * 0.07) * 0.5;
    worldPos.y += terrain;

    vWorldPosition = worldPos;
    gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);
}
`;

const floorFragmentShader = `
uniform float uTime;
uniform float uSeaSpeed;
uniform float uSeaFreq;
uniform float uSeaHeight;
uniform float uSeaChoppy;
uniform vec3 uSunDirection;
uniform float uFloorDepth;
uniform float uFloorBrightness;
uniform float uCausticIntensity;
uniform float uWaterClarity;
uniform sampler2D uRockTexture;
varying vec3 vWorldPosition;
varying vec2 vUv;

${oceanNoiseGLSL}
${getHeightGLSL(2)}

// Compute wave surface gradient at a given xz position
vec2 getGradient(vec2 xz, float eps) {
    float h0 = getHeight(xz);
    float hx = getHeight(xz + vec2(eps, 0.0));
    float hz = getHeight(xz + vec2(0.0, eps));
    return vec2(hx - h0, hz - h0) / eps;
}

// Jacobian-based caustic brightness
float getCaustic(vec2 xz, float scale) {
    float eps = 0.1;
    vec2 surfaceXZ = xz * scale;

    // Wave surface gradient
    vec2 grad = getGradient(surfaceXZ, eps);

    // Snell's law refraction: approximate displaced landing position
    float n_ratio = 1.0 / 1.33; // air to water
    vec2 refractOffset = grad * n_ratio;

    // Jacobian determinant of the refraction mapping
    vec2 grad_dx = (getGradient(surfaceXZ + vec2(eps, 0.0), eps) - grad) / eps;
    vec2 grad_dz = (getGradient(surfaceXZ + vec2(0.0, eps), eps) - grad) / eps;

    float j11 = 1.0 + n_ratio * grad_dx.x;
    float j12 = n_ratio * grad_dz.x;
    float j21 = n_ratio * grad_dx.y;
    float j22 = 1.0 + n_ratio * grad_dz.y;

    float det = abs(j11 * j22 - j12 * j21);

    // Inverse determinant = area contraction = caustic brightness
    return clamp(1.0 / (det + 0.001) - 0.8, 0.0, 3.0);
}

void main() {
    vec3 p = vWorldPosition;

    // Two caustic layers at different scales for richness
    float caustic1 = getCaustic(p.xz, 1.0);
    float caustic2 = getCaustic(p.xz, 0.5);
    float caustic = (caustic1 + caustic2 * 0.5) * 0.8;

    // Rock/sand base texture (tiled)
    vec2 texUV = vWorldPosition.xz * 0.04;
    vec3 rockColor = texture2D(uRockTexture, texUV).rgb;
    // Desaturate for sandy look
    float gray = dot(rockColor, vec3(0.299, 0.587, 0.114));
    vec3 sandColor = mix(vec3(gray), rockColor, 0.4);
    sandColor = sandColor * vec3(0.9, 0.85, 0.7); // warm sandy tint, brighter

    // Depth from water surface to this floor point
    float waterDepth = max(0.0, -p.y);

    // Absorption scaled by waterClarity (1=crystal clear, 0=murky)
    float absorptionScale = 1.0 - uWaterClarity * 0.9;
    vec3 absorption = exp(-waterDepth * vec3(0.025, 0.012, 0.005) * absorptionScale);

    // Sun light direction & intensity
    vec3 sunDir = normalize(uSunDirection);
    float sunIntensity = max(sunDir.y, 0.1);

    // Diffuse lighting scaled by floorBrightness
    float diffuse = max(dot(vec3(0.0, 1.0, 0.0), sunDir), 0.4);
    vec3 color = sandColor * (diffuse + 0.3) * uFloorBrightness;

    // Caustic contribution scaled by causticIntensity
    color += caustic * vec3(1.0, 1.0, 1.2) * sunIntensity * uCausticIntensity;

    // Apply absorption after all lighting
    color *= absorption;

    // Ambient underwater light so floor is never fully dark
    vec3 deepAmbient = vec3(0.05, 0.12, 0.2);
    float ambientFade = max(0.3, 1.0 - waterDepth * 0.005);
    color += deepAmbient * ambientFade * uFloorBrightness;

    // Distance fog matching scene fog: fade to blue within visibility range
    float distFromCamera = length(p - cameraPosition);
    float visFar = 10.0 + uWaterClarity * 50.0;
    float fogFactor = smoothstep(visFar * 0.1, visFar, distFromCamera);
    vec3 fogColor = vec3(0.01, 0.06, 0.14);
    color = mix(color, fogColor, fogFactor);

    gl_FragColor = vec4(color, 1.0);
}
`;

function OceanFloor({ sunPosition, seaSpeed, seaHeight, seaChoppy, seaFreq, floorDepth = -15, floorBrightness = 0.7, causticIntensity = 1.5, waterClarity = 0.5 }) {
  const ref = useRef();

  const rockTexture = useLoader(THREE.TextureLoader, '/rocks.jpg');

  const geom = useMemo(() => new THREE.PlaneGeometry(10000, 10000, 128, 128), []);

  useMemo(() => {
    rockTexture.wrapS = THREE.RepeatWrapping;
    rockTexture.wrapT = THREE.RepeatWrapping;
  }, [rockTexture]);

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uSunDirection: { value: new THREE.Vector3(0, 1, 0.8) },
    uSeaSpeed: { value: seaSpeed },
    uSeaHeight: { value: seaHeight },
    uSeaChoppy: { value: seaChoppy },
    uSeaFreq: { value: seaFreq },
    uFloorDepth: { value: floorDepth },
    uFloorBrightness: { value: floorBrightness },
    uCausticIntensity: { value: causticIntensity },
    uWaterClarity: { value: waterClarity },
    uRockTexture: { value: rockTexture },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  useFrame((state, delta) => {
    const mat = ref.current.material;
    mat.uniforms.uTime.value += delta;
    mat.uniforms.uSeaSpeed.value = seaSpeed;
    mat.uniforms.uSeaHeight.value = seaHeight;
    mat.uniforms.uSeaChoppy.value = seaChoppy;
    mat.uniforms.uSeaFreq.value = seaFreq;
    mat.uniforms.uFloorDepth.value = floorDepth;
    mat.uniforms.uFloorBrightness.value = floorBrightness;
    mat.uniforms.uCausticIntensity.value = causticIntensity;
    mat.uniforms.uWaterClarity.value = waterClarity;
    if (sunPosition) {
      mat.uniforms.uSunDirection.value.set(...sunPosition).normalize();
    }
  });

  return (
    <mesh ref={ref} geometry={geom} rotation-x={-Math.PI / 2} position={[0, floorDepth, 0]}>
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={floorVertexShader}
        fragmentShader={floorFragmentShader}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

export default OceanFloor;
